<template>
  <div class="manager-app">
    <div v-if="!authenticated" class="manager-login">
      <form class="manager-login-card" @submit.prevent="loginStep === 'email' ? sendOtp() : verifyOtp()">
        <div class="manager-login-brand">
          <span>W</span>
          <div><h1>Winelands</h1><p>Property management</p></div>
        </div>

        <div v-if="loginStep === 'email'">
          <h2>Sign in</h2>
          <label><span>Email address</span><input v-model.trim="email" type="email" placeholder="name@example.com" required></label>
          <button class="manager-button manager-button-primary manager-button-block" type="submit" :disabled="loginLoading">
            {{ loginLoading ? 'Sending…' : 'Send login code' }}
          </button>
        </div>

        <div v-else>
          <h2>Enter login code</h2>
          <p>We sent a six-digit code to {{ email }}.</p>
          <label><span>Login code</span><input v-model.trim="otp" inputmode="numeric" maxlength="6" placeholder="000000" required></label>
          <button class="manager-button manager-button-primary manager-button-block" type="submit" :disabled="loginLoading">
            {{ loginLoading ? 'Checking…' : 'Continue' }}
          </button>
          <button class="manager-link-button" type="button" @click="resetLogin">Use another email</button>
        </div>

        <div v-if="loginError" class="manager-notice manager-notice-error">{{ loginError }}</div>
      </form>
    </div>

    <div v-else class="manager-shell">
      <aside class="manager-sidebar">
        <div class="manager-brand">
          <span>W</span>
          <div><strong>Winelands</strong><small>{{ userEmail }}</small></div>
        </div>

        <nav>
          <button
            v-for="item in navItems"
            :key="item.key"
            type="button"
            :class="{ active: activeView === item.key }"
            @click="requestViewChange(item.key)"
          >
            {{ item.label }}
          </button>
        </nav>

        <button class="manager-logout" type="button" @click="requestLogout">Sign out</button>
      </aside>

      <main class="manager-main">
        <PropertyManager
          v-if="activeView === 'properties'"
          ref="activeManager"
          @request-navigation="requestEditorExit"
        />
        <SimpleManager
          v-else-if="activeView === 'agents'"
          ref="activeManager"
          resource="agent"
          @request-navigation="requestEditorExit"
        />
        <SimpleManager
          v-else
          ref="activeManager"
          resource="development"
          @request-navigation="requestEditorExit"
        />
      </main>
    </div>

    <div
      v-if="showUnsavedDialog"
      class="manager-dialog-backdrop"
      role="presentation"
      @click.self="keepEditing"
    >
      <section
        class="manager-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manager-unsaved-title"
        aria-describedby="manager-unsaved-description"
      >
        <h2 id="manager-unsaved-title">Unsaved changes</h2>
        <p id="manager-unsaved-description">
          You have edits that have not been saved. What would you like to do?
        </p>
        <div class="manager-dialog-actions">
          <button
            class="manager-button manager-button-primary"
            type="button"
            :disabled="savingUnsaved"
            @click="saveAndContinue"
          >
            {{ savingUnsaved ? 'Saving…' : 'Save and continue' }}
          </button>
          <button
            class="manager-button manager-button-danger"
            type="button"
            :disabled="savingUnsaved"
            @click="discardAndContinue"
          >
            Discard changes
          </button>
          <button
            class="manager-button manager-button-secondary"
            type="button"
            :disabled="savingUnsaved"
            @click="keepEditing"
          >
            Keep editing
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import PropertyManager from './components/PropertyManager.vue'
import SimpleManager from './components/SimpleManager.vue'

const OTP_URL = 'https://www.stratitude.co.za/api/chas_manager_otp.php'
const AUTH_KEY = 'winelands_manager_auth'

export default {
  name: 'ManagerApp',

  components: { PropertyManager, SimpleManager },

  data: function () {
    return {
      authenticated: false,
      userEmail: '',
      email: '',
      otp: '',
      loginStep: 'email',
      loginLoading: false,
      loginError: '',
      activeView: 'properties',
      showUnsavedDialog: false,
      savingUnsaved: false,
      pendingNavigation: null,
      navItems: [
        { key: 'properties', label: 'Properties' },
        { key: 'agents', label: 'Agents' },
        { key: 'developments', label: 'Developments' }
      ]
    }
  },

  mounted: function () {
    this.restoreAuth()
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },

  beforeDestroy: function () {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },

  beforeRouteLeave: function (to, from, next) {
    if (!this.currentManagerHasUnsavedChanges()) {
      next()
      return
    }

    this.openUnsavedDialog({ type: 'route', next: next })
  },

  methods: {
    currentManager: function () {
      return this.$refs.activeManager || null
    },

    currentManagerHasUnsavedChanges: function () {
      const manager = this.currentManager()
      return Boolean(
        manager &&
        typeof manager.hasUnsavedChanges === 'function' &&
        manager.hasUnsavedChanges()
      )
    },

    handleBeforeUnload: function (event) {
      if (!this.currentManagerHasUnsavedChanges()) return
      event.preventDefault()
      event.returnValue = ''
    },

    openUnsavedDialog: function (navigation) {
      this.pendingNavigation = navigation
      this.showUnsavedDialog = true
    },

    requestNavigation: function (navigation) {
      if (this.currentManagerHasUnsavedChanges()) {
        this.openUnsavedDialog(navigation)
        return
      }
      this.completeNavigation(navigation)
    },

    requestViewChange: function (view) {
      if (view === this.activeView) {
        const manager = this.currentManager()
        if (manager && typeof manager.isEditingView === 'function' && manager.isEditingView()) {
          this.requestEditorExit()
        }
        return
      }
      this.requestNavigation({ type: 'view', view: view })
    },

    requestEditorExit: function () {
      this.requestNavigation({ type: 'editor-list' })
    },

    requestLogout: function () {
      this.requestNavigation({ type: 'logout' })
    },

    saveAndContinue: async function () {
      const manager = this.currentManager()
      if (!manager || typeof manager.saveForNavigation !== 'function') {
        this.keepEditing()
        return
      }

      this.savingUnsaved = true
      let saved = false
      try {
        saved = await manager.saveForNavigation()
      } finally {
        this.savingUnsaved = false
      }

      if (!saved) {
        this.keepEditing()
        return
      }

      this.completeNavigation(this.takePendingNavigation())
    },

    discardAndContinue: function () {
      this.completeNavigation(this.takePendingNavigation())
    },

    keepEditing: function () {
      const navigation = this.takePendingNavigation()
      if (navigation && navigation.type === 'route' && typeof navigation.next === 'function') {
        navigation.next(false)
      }
    },

    takePendingNavigation: function () {
      const navigation = this.pendingNavigation
      this.pendingNavigation = null
      this.showUnsavedDialog = false
      return navigation
    },

    completeNavigation: function (navigation) {
      if (!navigation) return

      if (navigation.type === 'view') {
        this.activeView = navigation.view
        return
      }

      if (navigation.type === 'editor-list') {
        const manager = this.currentManager()
        if (manager && typeof manager.forceReturnToList === 'function') {
          manager.forceReturnToList()
        }
        return
      }

      if (navigation.type === 'logout') {
        this.performLogout()
        return
      }

      if (navigation.type === 'route' && typeof navigation.next === 'function') {
        navigation.next()
      }
    },

    restoreAuth: function () {
      const saved = localStorage.getItem(AUTH_KEY)
      if (!saved) return
      try {
        const auth = JSON.parse(saved)
        if (!auth.email || !auth.token || Date.now() > auth.expiresAt) throw new Error('Expired')
        this.authenticated = true
        this.userEmail = auth.email
      } catch (error) {
        localStorage.removeItem(AUTH_KEY)
      }
    },

    sendOtp: async function () {
      this.loginLoading = true
      this.loginError = ''
      try {
        const response = await axios.post(OTP_URL, { action: 'send', email: this.email })
        if (!response.data || !response.data.success) {
          throw new Error(response.data && response.data.message ? response.data.message : 'Could not send the login code.')
        }
        this.loginStep = 'otp'
      } catch (error) {
        this.loginError = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message
      } finally {
        this.loginLoading = false
      }
    },

    verifyOtp: async function () {
      this.loginLoading = true
      this.loginError = ''
      try {
        const response = await axios.post(OTP_URL, { action: 'verify', email: this.email, otp: this.otp })
        if (!response.data || !response.data.success || !response.data.token) {
          throw new Error(response.data && response.data.message ? response.data.message : 'The login code is not valid.')
        }
        const resolvedEmail = response.data.user && response.data.user.email ? response.data.user.email : this.email
        localStorage.setItem(AUTH_KEY, JSON.stringify({
          token: response.data.token,
          email: resolvedEmail,
          expiresAt: Date.now() + (24 * 60 * 60 * 1000)
        }))
        this.authenticated = true
        this.userEmail = resolvedEmail
      } catch (error) {
        this.loginError = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message
      } finally {
        this.loginLoading = false
      }
    },

    resetLogin: function () {
      this.loginStep = 'email'
      this.email = ''
      this.otp = ''
      this.loginError = ''
    },

    performLogout: function () {
      localStorage.removeItem(AUTH_KEY)
      this.authenticated = false
      this.userEmail = ''
      this.email = ''
      this.otp = ''
      this.loginStep = 'email'
      this.activeView = 'properties'
    }
  }
}
</script>

<style>
.manager-app {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(48, 86, 211, 0.08), transparent 30%),
    linear-gradient(180deg, #f5f7fb 0%, #eef2f8 100%);
  color: #18212f;
  font-family: Inter, Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
}

.manager-app *, .manager-app *::before, .manager-app *::after { box-sizing: border-box; }
.manager-app h1, .manager-app h2, .manager-app p { margin-top: 0; }
.manager-app button, .manager-app input, .manager-app select, .manager-app textarea { font: inherit; }

.manager-login { min-height: 100vh; display: grid; place-items: center; padding: 24px; }
.manager-login-card { width: 100%; max-width: 460px; background: rgba(255,255,255,.94); padding: 30px; border: 1px solid #dbe4f0; border-radius: 28px; box-shadow: 0 24px 55px rgba(15,23,42,.12); }
.manager-login-brand, .manager-brand { display: flex; align-items: center; gap: 12px; }
.manager-login-brand { margin-bottom: 36px; }
.manager-login-brand > span, .manager-brand > span { display: grid; place-items: center; width: 54px; height: 54px; border-radius: 16px; background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #fff; font-size: 20px; font-weight: 800; box-shadow: 0 12px 26px rgba(37,99,235,.35); }
.manager-login-brand h1 { margin: 0; font-size: 24px; }
.manager-login-brand p { margin: 2px 0 0; color: #687684; }
.manager-login-card h2 { font-size: 20px; margin-bottom: 20px; }
.manager-login-card label { display: block; margin-bottom: 20px; }
.manager-login-card label span { display: block; margin-bottom: 7px; font-weight: 600; }

.manager-shell { min-height: 100vh; display: grid; grid-template-columns: 290px minmax(0, 1fr); }
.manager-sidebar { position: sticky; top: 0; height: 100vh; display: flex; flex-direction: column; padding: 28px 20px; background: linear-gradient(180deg, #0f172a 0%, #111827 100%); color: #fff; box-shadow: 18px 0 40px rgba(15,23,42,.14); }
.manager-brand { padding: 0 8px 30px; border-bottom: 1px solid rgba(255,255,255,.16); }
.manager-brand > span { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #fff; }
.manager-brand strong, .manager-brand small { display: block; max-width: 145px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.manager-brand strong { font-size: 18px; }
.manager-brand small { margin-top: 3px; color: rgba(255,255,255,.7); }
.manager-sidebar nav { display: grid; gap: 6px; margin-top: 26px; }
.manager-sidebar nav button, .manager-logout { width: 100%; padding: 14px 16px; border: 0; border-radius: 16px; background: transparent; color: #cbd5e1; text-align: left; cursor: pointer; }
.manager-sidebar nav button:hover { background: rgba(255,255,255,.06); color: #fff; }
.manager-sidebar nav button.active { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #fff; box-shadow: 0 14px 28px rgba(37,99,235,.28); }
.manager-logout { margin-top: auto; border: 1px solid rgba(255,255,255,.25); text-align: center; }
.manager-main { min-width: 0; padding: 36px; }
.manager-section { max-width: 1450px; margin: 0 auto; }

.manager-page-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; margin-bottom: 28px; }
.manager-page-header h1 { margin-bottom: 4px; font-size: 30px; line-height: 1.2; }
.manager-page-header p { margin: 0; color: #687684; }
.manager-header-actions { display: flex; gap: 10px; }
.manager-back { margin: 0 0 14px; padding: 0; border: 0; background: none; color: #315f80; cursor: pointer; }

.manager-button { min-height: 42px; padding: 10px 16px; border: 1px solid transparent; border-radius: 14px; font-weight: 800; cursor: pointer; }
.manager-button:disabled { cursor: wait; opacity: .65; }
.manager-button-primary { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); border-color: #1d4ed8; color: #fff; }
.manager-button-secondary { background: #fff; border-color: #cbd5e1; color: #334155; }
.manager-button-danger { background: #fff; border-color: #bc3d3d; color: #a12f2f; }
.manager-button-block { width: 100%; }
.manager-link-button { display: block; margin: 18px auto 0; padding: 0; border: 0; background: none; color: #2563eb; cursor: pointer; }

.manager-toolbar { display: flex; align-items: center; gap: 12px; padding: 18px; margin-bottom: 18px; background: #fff; border: 1px solid #dbe4f0; border-radius: 18px; box-shadow: 0 12px 28px rgba(15,23,42,.05); }
.manager-app input, .manager-app select, .manager-app textarea { width: 100%; min-height: 44px; padding: 11px 14px; border: 1px solid #d4ddea; border-radius: 14px; background: #fff; color: #0f172a; }
.manager-app .manager-toolbar .manager-toolbar-search { flex: 1 1 auto; width: auto; min-width: 240px; }
.manager-app .manager-toolbar .manager-toolbar-filter { flex: 0 0 190px; width: 190px; }
.manager-app textarea { resize: vertical; }
.manager-app input:focus, .manager-app select:focus, .manager-app textarea:focus { outline: none; border-color: #93c5fd; box-shadow: 0 0 0 4px rgba(37,99,235,.10); }

.manager-table-wrap { overflow-x: auto; background: #fff; border: 1px solid #dbe4f0; border-radius: 20px; box-shadow: 0 12px 28px rgba(15,23,42,.05); }
.manager-table { width: 100%; border-collapse: collapse; }
.manager-table th, .manager-table td { padding: 15px 18px; border-bottom: 1px solid #e8ecef; text-align: left; vertical-align: middle; }
.manager-table th { background: #f8f9fa; color: #596775; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; }
.manager-table tbody tr:last-child td { border-bottom: 0; }
.manager-table td span { display: block; margin-top: 3px; color: #71808d; font-size: 13px; }
.manager-table-action { width: 90px; text-align: right !important; }
.manager-status { display: inline-block !important; margin: 0 !important; padding: 4px 8px; border-radius: 30px; background: #eaf2ec; color: #2f6840 !important; font-weight: 700; }
.manager-empty { padding: 42px !important; color: #71808d; text-align: center !important; }

.manager-notice { padding: 12px 15px; margin-bottom: 18px; border-radius: 4px; }
.manager-notice-success { background: #eaf5ed; color: #28613a; }
.manager-notice-warning { background: #fff5d8; color: #7a4a00; border: 1px solid #f2d58a; }
.manager-notice-error { background: #faeaea; color: #932f2f; }

.manager-editor-tabs { display: flex; gap: 8px; padding: 8px; margin-bottom: 20px; overflow-x: auto; background: #fff; border: 1px solid #dbe4f0; border-radius: 18px; box-shadow: 0 12px 28px rgba(15,23,42,.05); }
.manager-editor-tabs button { flex: 1 0 auto; min-height: 42px; padding: 10px 18px; border: 0; border-radius: 12px; background: transparent; color: #64748b; font-weight: 800; cursor: pointer; }
.manager-editor-tabs button:hover { background: #f1f5f9; color: #0f172a; }
.manager-editor-tabs button.active { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #fff; box-shadow: 0 8px 18px rgba(37,99,235,.22); }

.manager-form { display: grid; gap: 20px; }
.manager-form-section { padding: 26px; background: #fff; border: 1px solid #dbe4f0; border-radius: 20px; box-shadow: 0 12px 28px rgba(15,23,42,.05); }
.manager-form-section h2 { padding-bottom: 14px; margin-bottom: 22px; border-bottom: 1px solid #e8ecef; font-size: 18px; }
.manager-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.manager-form-grid-four { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.manager-property-count-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.manager-size-pairs { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; margin-top: 20px; }
.manager-size-pair { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(190px, .8fr); gap: 14px; padding: 18px; border: 1px solid #e2e8f0; border-radius: 16px; background: #f8fafc; }
.manager-form label > span { display: block; margin-bottom: 7px; color: #465461; font-weight: 700; }
.manager-field-wide { grid-column: 1 / -1; }
.manager-checkbox { display: flex; align-items: center; gap: 10px; min-height: 42px; padding-top: 26px; }
.manager-checkbox input { width: 18px; min-height: 18px; margin: 0; }
.manager-checkbox span { margin: 0 !important; }
.manager-public-strip-options > strong, .manager-public-strip-options > small { display: block; }
.manager-public-strip-options > strong { color: #465461; }
.manager-public-strip-options > small { margin-top: 4px; color: #64748b; }
.manager-inline-checks { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.manager-inline-checks label { display: inline-flex; align-items: center; gap: 8px; min-height: 40px; padding: 9px 12px; border: 1px solid #dbe4f0; border-radius: 12px; background: #f8fafc; color: #465461; font-weight: 700; cursor: pointer; }
.manager-inline-checks input { width: 18px !important; min-height: 18px !important; margin: 0; }
.manager-inline-checks span { margin: 0 !important; }
.manager-prefix-input { display: flex; align-items: stretch; }
.manager-prefix-input strong { display: flex; align-items: center; padding: 0 12px; border: 1px solid #d4ddea; border-right: 0; border-radius: 14px 0 0 14px; background: #eef3f9; color: #334155; }
.manager-prefix-input input { border-radius: 0 14px 14px 0; }
.manager-image-manager { min-width: 0; }
.manager-image-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; margin-bottom: 14px; }
.manager-image-heading strong, .manager-image-heading small { display: block; }
.manager-image-heading strong { color: #465461; }
.manager-image-heading small { margin-top: 4px; color: #64748b; }
.manager-folder-label { flex: 0 0 auto; padding: 6px 10px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 12px; font-weight: 800; }
.manager-upload-dropzone { position: relative; display: flex; min-height: 130px; align-items: center; justify-content: center; flex-direction: column; padding: 24px; border: 2px dashed #93c5fd; border-radius: 18px; background: #f8fbff; color: #1d4ed8; text-align: center; cursor: pointer; transition: .2s ease; }
.manager-upload-dropzone:hover { border-color: #2563eb; background: #eff6ff; }
.manager-upload-dropzone.disabled { cursor: wait; opacity: .7; }
.manager-upload-dropzone.locked { cursor: not-allowed; border-color: #cbd5e1; background: #f1f5f9; color: #64748b; }
.manager-upload-dropzone-small { min-height: 100px; margin-top: 14px; }
.manager-upload-dropzone input { position: absolute; width: 1px; height: 1px; min-height: 1px; opacity: 0; pointer-events: none; }
.manager-upload-dropzone strong, .manager-upload-dropzone span { display: block; }
.manager-upload-dropzone span { margin-top: 5px; color: #64748b; font-size: 13px; }
.manager-upload-progress { height: 8px; margin-top: 12px; overflow: hidden; border-radius: 999px; background: #dbeafe; }
.manager-upload-progress span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #2563eb, #1d4ed8); transition: width .15s ease; }
.manager-property-images { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-top: 20px; }
.manager-property-image { position: relative; overflow: hidden; border: 1px solid #dbe4f0; border-radius: 16px; background: #fff; }
.manager-property-image img { display: block; width: 100%; height: 170px; object-fit: cover; }
.manager-cover-badge { position: absolute; top: 10px; left: 10px; padding: 5px 9px; border-radius: 999px; background: #2563eb; color: #fff; font-size: 11px; font-weight: 800; box-shadow: 0 5px 12px rgba(15,23,42,.18); }
.manager-image-actions { display: flex; gap: 6px; padding: 9px; }
.manager-image-actions button { flex: 1; padding: 7px 8px; border: 1px solid #cbd5e1; border-radius: 9px; background: #fff; color: #334155; font-size: 11px; font-weight: 800; cursor: pointer; }
.manager-image-actions button.danger { border-color: #fecaca; color: #b91c1c; }
.manager-image-actions button:disabled { opacity: .6; cursor: wait; }
.manager-image-empty { margin: 14px 0 0; padding: 18px; border-radius: 14px; background: #f8fafc; color: #64748b; text-align: center; }
.manager-agent-photo-preview { display: flex; align-items: center; gap: 18px; padding: 16px; border: 1px solid #dbe4f0; border-radius: 18px; background: #f8fbff; }
.manager-agent-photo-preview img { display: block; width: 150px; height: 150px; border-radius: 16px; object-fit: cover; background: #e2e8f0; }
.manager-form-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 4px 0 30px; }
.manager-dialog-backdrop { position: fixed; z-index: 10000; inset: 0; display: grid; place-items: center; padding: 24px; background: rgba(15,23,42,.62); }
.manager-dialog { width: 100%; max-width: 520px; padding: 28px; border: 1px solid #dbe4f0; border-radius: 22px; background: #fff; box-shadow: 0 28px 70px rgba(15,23,42,.28); }
.manager-dialog h2 { margin-bottom: 8px; font-size: 23px; }
.manager-dialog p { margin-bottom: 24px; color: #64748b; }
.manager-dialog-actions { display: flex; flex-wrap: wrap; gap: 10px; }

@media (max-width: 900px) {
  .manager-shell { display: block; }
  .manager-sidebar { position: static; width: 100%; height: auto; padding: 18px; }
  .manager-brand { padding-bottom: 16px; }
  .manager-sidebar nav { grid-template-columns: repeat(3, 1fr); margin-top: 16px; }
  .manager-sidebar nav button { text-align: center; }
  .manager-logout { margin-top: 14px; }
  .manager-main { padding: 24px 18px; }
  .manager-form-grid-four { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .manager-property-count-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .manager-size-pairs { grid-template-columns: 1fr; }
  .manager-property-images { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 600px) {
  .manager-page-header { align-items: stretch; flex-direction: column; }
  .manager-header-actions { width: 100%; }
  .manager-header-actions .manager-button { flex: 1; }
  .manager-toolbar { flex-direction: column; }
  .manager-app .manager-toolbar .manager-toolbar-search,
  .manager-app .manager-toolbar .manager-toolbar-filter { flex: 1 1 auto; width: 100%; min-width: 0; }
  .manager-form-grid, .manager-form-grid-four, .manager-property-count-grid { grid-template-columns: 1fr; }
  .manager-size-pair { grid-template-columns: 1fr; }
  .manager-field-wide { grid-column: auto; }
  .manager-form-section { padding: 20px; }
  .manager-property-images { grid-template-columns: 1fr; }
  .manager-agent-photo-preview { align-items: stretch; flex-direction: column; }
  .manager-agent-photo-preview img { width: 100%; height: 260px; }
  .manager-dialog-actions { flex-direction: column; }
  .manager-dialog-actions .manager-button { width: 100%; }
}
</style>
